import * as React from "react";

import { DateRangePicker, DateRangePickerProps } from "./DateRangePicker";

interface State {
  isFocused: boolean;
}

type SetState = React.Dispatch<React.SetStateAction<State>>;

type BtnRef = React.MutableRefObject<HTMLButtonElement>;

interface DateRangePickerBtnProps {
  id: string;
  renderDatePicker: (DatePicker: typeof DateRangePicker) => React.ReactNode;
  renderBtn: (setState: SetState, btnRef: BtnRef) => React.ReactNode;
}

type DateRangePickerBtnPassedRef = {
  dialog: HTMLDivElement;
  button: HTMLButtonElement;
};

export const DateRangePickerBtn = React.forwardRef<
  DateRangePickerBtnPassedRef,
  DateRangePickerBtnProps
>(({ id, renderDatePicker, renderBtn }, ref) => {
  // only load datepicker on client, see useEffect below
  const [show, setShow] = React.useState(false);

  const [state, setState] = React.useState<State>(() => {
    return {
      isFocused: false,
    };
  });

  const dialogRef = React.useRef<HTMLDivElement>(null);
  const btnRef = React.useRef<HTMLButtonElement>(null);

  React.useImperativeHandle(ref, () => ({
    dialog: dialogRef.current,
    button: btnRef.current,
  }));

  React.useEffect(() => {
    if (state.isFocused) {
      // TODO : see if we can get the current today el better
      const el: HTMLDivElement = document.querySelector(
        ".DayPicker-Day--today"
      );

      if (el) {
        el.focus();
      }
    }
  }, [state.isFocused]);

  React.useEffect(() => {
    /**
     * We only want to render this on the client and not server,
     * as server time will be used and react mismatch occurs favouring server time
     */
    setShow(true);

    const handleBodyClick = (e: MouseEvent): void => {
      if (
        !dialogRef.current.contains(e.target as Node) &&
        !btnRef.current.contains(e.target as Node)
      ) {
        setState((prev) => ({
          ...prev,
          isFocused: false,
        }));
      }
    };

    const handleKeyPress = (e: KeyboardEvent): void => {
      const { key } = e;
      if (key === "Escape") {
        setState((prev) => ({
          ...prev,
          isFocused: false,
        }));
      }
    };

    document.addEventListener("click", handleBodyClick, true);
    window.addEventListener("keydown", handleKeyPress, true);

    return (): void => {
      document.removeEventListener("click", handleBodyClick, true);
      window.removeEventListener("keydown", handleKeyPress, true);
    };
  }, []);

  return (
    <>
      <div
        css={`
          display: inline;
          position: relative;
        `}
      >
        {renderBtn(setState, btnRef)}
        {show && (
          <section
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${id}__heading`}
            className="p-3 border shadow rounded"
            style={{
              display: state.isFocused ? "block" : "none",
              visibility: state.isFocused ? "visible" : "hidden",
            }}
            css={`
              position: absolute;
              right: 0;
              top: 100%;
              background-color: #fff;
              z-index: 1;
            `}
          >
            {renderDatePicker(DateRangePicker)}
          </section>
        )}
      </div>
    </>
  );
});
